/**
 * InteractiveParticles
 *
 * Image-based interactive particle system.
 * https://github.com/brunoimbrizi/interactive-particles
 *
 * Optimisations:
 * - Geometry built once; never rebuilt on resize (camera handles it)
 * - mousemove throttled to one trail update per rAF frame
 * - getBoundingClientRect cached, only refreshed on resize
 * - Render drops to ~30 fps when idle (breathing only), 60 fps when active
 * - rAF paused when tab is hidden
 * - Pixel sampling deferred via setTimeout to yield first render frame
 */

import { useEffect, useRef } from "react";
import * as THREE from "three";

// ── Constants ─────────────────────────────────────────────────────────────────

const TRAIL_LEN = 64;
const DECAY_MS  = 300;
const FOV       = 50;
const TAN_HALF  = Math.tan(((FOV * Math.PI) / 180) / 2);

// Baked at module level — uploaded to GPU once, never change
// Comet profile: strong head, long gradual tail stretched over 64 points
const TRAIL_WEIGHTS: number[] = [
  1.00, 0.98, 0.97, 0.95, 0.93, 0.91, 0.88, 0.86,
  0.83, 0.80, 0.77, 0.74, 0.71, 0.68, 0.64, 0.61,
  0.57, 0.54, 0.50, 0.47, 0.43, 0.40, 0.36, 0.33,
  0.30, 0.27, 0.24, 0.21, 0.18, 0.16, 0.14, 0.12,
  0.10, 0.08, 0.07, 0.06, 0.05, 0.04, 0.03, 0.025,
  0.020, 0.016, 0.013, 0.010, 0.008, 0.006, 0.005, 0.004,
  0.003, 0.002, 0.0015, 0.001, 0.0008, 0.0005, 0.0003, 0.0002,
  0.00015, 0.0001, 0.00007, 0.00004, 0.00002, 0.00001, 0.000005, 0.000001,
];

// ── Shaders ───────────────────────────────────────────────────────────────────

const VERTEX = /* glsl */ `
  #define TRAIL ${TRAIL_LEN}

  attribute float aRand;
  attribute vec3  aColor;

  uniform float uTime;
  uniform float uSize;
  uniform float uDepth;
  uniform float uCameraZ;
  uniform vec2  uTrail[TRAIL];
  uniform float uTrailW[TRAIL];
  uniform float uTrailDecay;
  uniform float uRepulseRadius;
  uniform float uRepulseStrength;

  varying float vRepulse;
  varying float vBreathe;
  varying vec3  vColor;

  void main() {
    vec3 pos = position;

    pos.z += (aRand - 0.5) * uDepth;
    float breathe = sin(uTime * 0.00075 + aRand * 6.2832) * 0.5 + 0.5;
    pos.z += breathe * 18.0;

    vec2  totalPush  = vec2(0.0);
    float maxFalloff = 0.0;

    for (int i = 0; i < TRAIL; i++) {
      vec2  diff = pos.xy - uTrail[i];
      float dist = length(diff);
      float w    = i == 0 ? uTrailW[0] : uTrailW[i] * uTrailDecay;
      float t    = 1.0 - clamp(dist / uRepulseRadius, 0.0, 1.0);
      float f    = t * t * t * w;
      totalPush   += (dist > 0.001 ? normalize(diff) : vec2(1.0, 0.0)) * f;
      maxFalloff   = max(maxFalloff, f);
    }

    pos.xy += totalPush * uRepulseStrength;
    pos.z  += maxFalloff * uRepulseStrength * 0.25;

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

    float sq     = maxFalloff * maxFalloff;
    float sz     = uSize * (0.65 + breathe * 0.35) + uSize * sq * 1.4;
    gl_PointSize = sz * (uCameraZ / -mvPosition.z);
    gl_Position  = projectionMatrix * mvPosition;

    vRepulse = maxFalloff;
    vBreathe = breathe;
    vColor   = aColor;
  }
`;

const FRAGMENT = /* glsl */ `
  varying float vRepulse;
  varying float vBreathe;
  varying vec3  vColor;

  void main() {
    float d = length(gl_PointCoord - 0.5) * 2.0;
    if (d > 1.0) discard;

    float sq    = vRepulse * vRepulse;
    float alpha = (1.0 - d * d) * (0.7 + vBreathe * 0.2 + sq * 0.1);
    vec3  col   = mix(vColor, vec3(1.0), sq * 0.25);

    gl_FragColor = vec4(col, alpha);
  }
`;

// ── Particle sampling ─────────────────────────────────────────────────────────

function sampleImage(img: HTMLImageElement, worldHeight: number) {
  const STEP      = 3;
  const BG_CUTOFF = 0.82;

  const canvas  = document.createElement("canvas");
  canvas.width  = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx     = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);

  const CW = canvas.width;
  const CH = canvas.height;
  const px = ctx.getImageData(0, 0, CW, CH).data;

  const targetH = worldHeight * 0.82;
  const targetW = targetH * (CW / CH);

  // Pre-allocate worst-case — avoids repeated array growth
  const maxPts    = Math.ceil(CW / STEP) * Math.ceil(CH / STEP);
  const positions = new Float32Array(maxPts * 3);
  const colors    = new Float32Array(maxPts * 3);
  const randoms   = new Float32Array(maxPts);
  let   count     = 0;

  for (let y = 0; y < CH; y += STEP) {
    for (let x = 0; x < CW; x += STEP) {
      const i          = (y * CW + x) * 4;
      const r          = px[i]     / 255;
      const g          = px[i + 1] / 255;
      const b          = px[i + 2] / 255;
      const a          = px[i + 3] / 255;

      if (r * 0.299 + g * 0.587 + b * 0.114 > BG_CUTOFF || a < 0.1) continue;

      const j          = count * 3;
      positions[j]     =  (x / (CW - 1) - 0.5) * targetW;
      positions[j + 1] = -(y / (CH - 1) - 0.5) * targetH;
      positions[j + 2] = 0;
      colors[j]        = r;
      colors[j + 1]    = g;
      colors[j + 2]    = b;
      randoms[count]   = Math.random();
      count++;
    }
  }

  return {
    positions: positions.subarray(0, count * 3),
    colors:    colors.subarray(0, count * 3),
    randoms:   randoms.subarray(0, count),
    count,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props { className?: string }

export function InteractiveParticles({ className }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let animId:   number;
    let destroyed = false;
    let width     = container.clientWidth;
    let height    = container.clientHeight;

    // Cached rect — refreshed on resize only, not per mousemove
    let rect = container.getBoundingClientRect();

    // ── Renderer ──────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x171717, 1);
    container.appendChild(renderer.domElement);

    // ── Scene & Camera ────────────────────────────────────────────────────────
    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(FOV, width / height, 0.1, 1000);

    // At the lg breakpoint the right panel is ~464px wide — fully zoomed out (550).
    // Scales in as the panel grows. Clamped to [250, 550].
    const fitCameraZ = () => Math.max(250, Math.min(550, Math.round(464 * 550 / width)));

    let targetCameraZ = fitCameraZ();
    let cameraZ       = targetCameraZ;
    camera.position.z = cameraZ;

    const vH = () => 2 * TAN_HALF * cameraZ;
    const vW = () => vH() * (width / height);

    // ── Trail ─────────────────────────────────────────────────────────────────
    const OFF  = 99999;
    const trail = Array.from({ length: TRAIL_LEN }, () => new THREE.Vector2(OFF, OFF));
    let lastMoveTime = -Infinity;
    let pendingMouse: { x: number; y: number } | null = null;

    // ── Material ──────────────────────────────────────────────────────────────
    const material = new THREE.ShaderMaterial({
      vertexShader:   VERTEX,
      fragmentShader: FRAGMENT,
      uniforms: {
        uTime:            { value: 0 },
        uSize:            { value: 3.0 },
        uDepth:           { value: 60 },
        uCameraZ:         { value: cameraZ },
        uTrail:           { value: trail },
        uTrailW:          { value: TRAIL_WEIGHTS },
        uTrailDecay:      { value: 0 },
        uRepulseRadius:   { value: 7 },
        uRepulseStrength: { value: 18 },
      },
      transparent: true,
      depthWrite:  false,
    });

    // ── Geometry — built once, never rebuilt on resize ─────────────────────────
    let geometry: THREE.BufferGeometry | null = null;
    let points:   THREE.Points         | null = null;

    function buildGeometry(img: HTMLImageElement) {
      if (destroyed) return;
      geometry?.dispose();
      if (points) scene.remove(points);

      const data = sampleImage(img, vH());
      if (data.count === 0) return;

      geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(data.positions, 3));
      geometry.setAttribute("aColor",   new THREE.BufferAttribute(data.colors,    3));
      geometry.setAttribute("aRand",    new THREE.BufferAttribute(data.randoms,   1));

      points = new THREE.Points(geometry, material);
      scene.add(points);
    }

    // Defer sampling so the first rAF frame renders before the CPU work hits
    const img = new Image();
    img.onload = () => setTimeout(() => buildGeometry(img), 0);
    img.src    = "/images/sneaker-particles.png";

    // ── Input ─────────────────────────────────────────────────────────────────
    function onMouseMove(e: MouseEvent) {
      pendingMouse = { x: e.clientX, y: e.clientY };
    }

    function onMouseLeave() {
      pendingMouse = null;
      for (const p of trail) p.set(OFF, OFF);
    }

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      targetCameraZ = Math.max(80, Math.min(500, targetCameraZ + e.deltaY * 0.4));
    }

    container.addEventListener("mousemove",  onMouseMove);
    container.addEventListener("mouseleave", onMouseLeave);
    container.addEventListener("wheel",      onWheel, { passive: false });

    // ── Resize — update camera only, geometry stays intact ────────────────────
    let isVisible = width > 0 && height > 0;

    const ro = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      const nowVisible = w > 0 && h > 0;

      if (nowVisible && !isVisible) {
        // Panel became visible — resume
        isVisible = true;
        last = performance.now();
        animate();
      } else if (!nowVisible && isVisible) {
        // Panel hidden — pause
        isVisible = false;
        cancelAnimationFrame(animId);
        return;
      }

      if (!nowVisible) return;

      width  = w;
      height = h;
      rect   = container.getBoundingClientRect();
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      targetCameraZ = fitCameraZ();
    });
    ro.observe(container);

    // ── Page visibility — pause when hidden ───────────────────────────────────
    function onVisibility() {
      if (document.hidden) {
        cancelAnimationFrame(animId);
      } else {
        last = performance.now();
        animate();
      }
    }
    document.addEventListener("visibilitychange", onVisibility);

    // ── Animation loop ────────────────────────────────────────────────────────
    let last      = performance.now();
    let skipFrame = false;

    function animate() {
      animId = requestAnimationFrame(animate);
      const now = performance.now();
      const dt  = now - last;
      last = now;

      // One trail update per frame max
      if (pendingMouse) {
        const ndcX = ((pendingMouse.x - rect.left) / rect.width)  * 2 - 1;
        const ndcY = -((pendingMouse.y - rect.top)  / rect.height) * 2 + 1;
        for (let i = TRAIL_LEN - 1; i > 0; i--) trail[i].copy(trail[i - 1]);
        trail[0].set(ndcX * vW() * 0.5, ndcY * vH() * 0.5);
        lastMoveTime = now;
        pendingMouse = null;
      }

      const trailDecay = Math.max(0, 1 - (now - lastMoveTime) / DECAY_MS);
      const zoomActive = Math.abs(targetCameraZ - cameraZ) > 0.5;

      // ~30 fps when idle — breathing is slow enough to look fine at half rate
      if (!trailDecay && !zoomActive) {
        skipFrame = !skipFrame;
        if (skipFrame) return;
      }

      material.uniforms.uTime.value      += dt;
      material.uniforms.uTrailDecay.value = trailDecay;

      cameraZ += (targetCameraZ - cameraZ) * 0.08;
      camera.position.z                = cameraZ;
      material.uniforms.uCameraZ.value = cameraZ;

      renderer.render(scene, camera);
    }

    if (isVisible) animate();

    return () => {
      destroyed = true;
      cancelAnimationFrame(animId);
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      container.removeEventListener("mousemove",  onMouseMove);
      container.removeEventListener("mouseleave", onMouseLeave);
      container.removeEventListener("wheel",      onWheel);
      geometry?.dispose();
      material.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: "100%", height: "100%" }}
    />
  );
}
